import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics'
import { useCanApproveEstimate } from '@/lib/auth'
import { formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'

// ─────────────────────────────────────────────────────────────────────
// Types
//
// `parts` and `collected` are JSONB columns. The worker passes them
// through unmodified (Neon parses to JS objects/arrays). The TypeScript
// types here mark the fields the UI knows about today as known and
// everything else as `unknown` via the `[key: string]: unknown` index
// signature, so PIES-aligned future fields (category_code,
// subcategory_code, position, attributes, oe_interchanges, vendor,
// is_oem, hazmat_flags, etc.) flow through transparently.

type PartItem = {
  brand?: string | null
  part_number?: string | null
  description?: string | null
  qty?: number | null
  price_each?: number | null
  availability?: string | null
  // PIES-aligned optional extensions — kept as `?` so the UI can render
  // only what's present today and absorb new fields without a type bump:
  category_code?: string | null
  subcategory_code?: string | null
  position?: string | null
  attributes?: Record<string, unknown> | null
  oe_interchanges?: string[] | null
  vendor?: string | null
  is_oem?: boolean | null
  hazmat_flags?: string[] | null
  // Forward-compat catch-all
  [key: string]: unknown
}

type PartsBundle = {
  source?: string | null
  primary?: PartItem[] | null
  alternative?: PartItem[] | null
  [key: string]: unknown
}

type Collected = {
  name?: string | null
  email?: string | null
  vin?: string | null
  [key: string]: unknown
}

type EstimateStatus =
  | 'pending_approval'
  | 'awaiting_customer_info'
  | 'approved'
  | 'declined'
  | 'expired'
  | (string & {})

type LaborSource =
  | 'matrix'
  | 'mechanic'
  | 'manual'
  | 'skipped'
  | 'worldpac' // legacy — normalized to "matrix" for display
  | (string & {})

type Estimate = {
  id: string
  status: EstimateStatus
  customer_id: string | null
  vehicle_id: string | null
  work_description: string | null
  labor_hours: number | null
  labor_rate: number | null
  labor_subtotal: number | null
  labor_source: LaborSource | null
  labor_confidence: string | null
  parts: PartsBundle | null
  parts_subtotal: number | null
  subtotal: number | null
  tax: number | null
  total: number | null
  currency: string | null
  flag: string | null
  collected: Collected | null
  time_remaining: string | null
  customer_phone_pending: string | null
  created_at: string
  customer_name: string | null
  customer_phone: string | null
  customer_email: string | null
  vehicle_year: number | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_engine: string | null
  vehicle_vin: string | null
}

// ─────────────────────────────────────────────────────────────────────
// Status pill

// Visual styling only; chip display label is looked up at render time
// via `t('common:status.<key>')` so it translates with the rest of the
// UI.
type StatusStyle = { chip: string; labelKey: string }

// DESIGN.md §4.9: green is STRICTLY saved-and-undoable, so no estimate
// lifecycle enum qualifies — statuses are either amber ("waiting on
// you": pending_approval, awaiting_customer_info) or neutral facts
// (draft, approved, declined, expired).
const CHIP_NEUTRAL = 'border-border bg-muted text-muted-foreground'
const CHIP_ATTENTION =
  'border-attention-line bg-attention-soft text-attention-text'

const STATUS_STYLES: Record<string, StatusStyle> = {
  draft: {
    labelKey: 'status.draft',
    chip: CHIP_NEUTRAL,
  },
  pending_approval: {
    labelKey: 'status.pending_approval',
    chip: CHIP_ATTENTION,
  },
  awaiting_customer_info: {
    labelKey: 'status.awaiting_reply',
    chip: CHIP_ATTENTION,
  },
  approved: {
    labelKey: 'status.approved',
    chip: CHIP_NEUTRAL,
  },
  declined: {
    labelKey: 'status.declined',
    chip: CHIP_NEUTRAL,
  },
  expired: {
    labelKey: 'status.expired',
    chip: CHIP_NEUTRAL,
  },
}

function statusStyle(status: string): StatusStyle | null {
  return STATUS_STYLES[status] ?? null
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation('common')
  const s = statusStyle(status)
  const chip = s?.chip ?? CHIP_NEUTRAL
  const label = s ? t(s.labelKey) : status
  return (
    <span
      className={`inline-flex items-center rounded-full border px-[9px] py-[3px] text-[12px] font-[650] ${chip}`}
    >
      {label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Labor-source chip

function laborSourceChip(
  t: TFunction<'common'>,
  source: LaborSource | null,
): {
  label: string
  className: string
} {
  // Normalize the legacy "worldpac" value per the architecture
  // correction in WORLDPAC_SPIKE_FINDINGS.md — labor never came from
  // the WorldPac API, it came from speedDIAL UI lookups, which is
  // matrix-equivalent for our purposes.
  const normalized = source === 'worldpac' ? 'matrix' : source
  // §4.9: a labor source is a fact, not a save and not a decision — every
  // variant renders the neutral chip; the label alone differentiates.
  switch (normalized) {
    case 'matrix':
      return {
        label: t('labor_source.matrix'),
        className: CHIP_NEUTRAL,
      }
    case 'mechanic':
      return {
        label: t('labor_source.mechanic'),
        className: CHIP_NEUTRAL,
      }
    case 'manual':
      return {
        label: t('labor_source.manual'),
        className: CHIP_NEUTRAL,
      }
    case 'skipped':
      return {
        label: t('labor_source.skipped'),
        className: CHIP_NEUTRAL,
      }
    default:
      // Unknown sources (incl. legacy "AllData" from older seed data)
      // — render the raw value as a neutral chip rather than a missing
      // translation key.
      return {
        label: source ?? '—',
        className: CHIP_NEUTRAL,
      }
  }
}

// ─────────────────────────────────────────────────────────────────────
// Page

const PENDING_STATUSES = new Set(['pending_approval', 'awaiting_customer_info'])

export function Estimates() {
  const { t } = useTranslation('estimates')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(timer)
  }, [toast])

  const estimatesQuery = useQuery({
    queryKey: ['estimates'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/estimates', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Estimate[]
    },
  })

  const estimates = useMemo(
    () => estimatesQuery.data ?? [],
    [estimatesQuery.data],
  )
  const pendingCount = useMemo(
    () => estimates.filter((e) => PENDING_STATUSES.has(e.status as string)).length,
    [estimates],
  )

  // estimate.list_viewed — fires once per query success, not per render.
  // Dedupes on data identity (TanStack returns the same array reference
  // until refetch), so a re-render with cached data is a no-op.
  const trackedDataRef = useRef<Estimate[] | null>(null)
  useEffect(() => {
    if (!estimatesQuery.data) return
    if (trackedDataRef.current === estimatesQuery.data) return
    trackedDataRef.current = estimatesQuery.data
    track('estimate.list_viewed', {
      total_count: estimatesQuery.data.length,
      pending_count: pendingCount,
    })
  }, [estimatesQuery.data, pendingCount])

  const toggle = (id: string) =>
    setExpandedId((current) => {
      // estimate.expanded fires only on open (current !== id), not on
      // close. Per docs/tracking-plan.md §4: "User clicks an EstimateCard
      // row in src/pages/Estimates.tsx, toggling expandedId."
      if (current !== id) {
        const est = estimates.find((e) => e.id === id)
        if (est) {
          track('estimate.expanded', {
            estimate_id: est.id,
            status: est.status,
            has_parts: (est.parts?.primary?.length ?? 0) > 0,
            has_labor: est.labor_hours !== null,
            total_amount: est.total ?? 0,
            labor_source: est.labor_source ?? null,
          })
        }
      }
      return current === id ? null : id
    })
  const showToast = () => setToast(t('pending.stub_toast'))

  return (
    // §3.2 content column — the pending body is two-column at md+, so the
    // list page gets the ≥1260px two-column width (660 → 720 → 1020).
    <div className="mx-auto w-full max-w-[660px] space-y-4 lg:max-w-[720px] min-[1260px]:max-w-[1020px]">
      {/* §4.5 page header — title + single primary action in the right slot. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', { count: pendingCount })}
          </p>
        </div>
        <Button asChild size="pill">
          <Link to="/estimates/new">
            <Plus className="size-4" />
            {t('header.new_estimate')}
          </Link>
        </Button>
      </div>

      {estimatesQuery.isError ? (
        <div className="rounded-[14px] border border-destructive/25 bg-destructive-soft p-4 text-sm text-destructive">
          {estimatesQuery.error instanceof Error
            ? estimatesQuery.error.message
            : t('error')}
        </div>
      ) : null}

      {estimates.length === 0 && !estimatesQuery.isLoading ? (
        <div className="rounded-[14px] border border-dashed border-border bg-muted/30 p-12 text-center text-sm text-muted-foreground">
          {t('empty')}
        </div>
      ) : (
        // §4.8 — one bordered 14px container, hairline-separated rows.
        <div className="overflow-hidden rounded-[14px] border border-border bg-background">
          {estimates.map((est) => (
            <EstimateCard
              key={est.id}
              estimate={est}
              expanded={expandedId === est.id}
              onToggle={() => toggle(est.id)}
              onStubAction={showToast}
            />
          ))}
        </div>
      )}

      {toast ? (
        // §4.10 toast — ink pill above the bottom stack, ephemeral only.
        <div
          role="status"
          className="fixed inset-x-0 bottom-[calc(var(--shell-bottom-inset,0px)+16px)] z-50 mx-auto w-fit max-w-[calc(100%-32px)] rounded-full bg-primary px-4 py-2.5 text-[14px] font-[550] text-primary-foreground shadow-2"
        >
          {toast}
        </div>
      ) : null}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Card

function EstimateCard({
  estimate,
  expanded,
  onToggle,
  onStubAction,
}: {
  estimate: Estimate
  expanded: boolean
  onToggle: () => void
  onStubAction: () => void
}) {
  const { t } = useTranslation('estimates')
  const customerName = estimate.customer_name
  const vehicleLabel = vehicleSummary(t, estimate)
  const isDraft = estimate.status === 'draft'

  // Drafts open straight to the detail page where they can be sent to
  // the customer; the inline-expand bodies (PendingBody / AwaitingBody)
  // are tied to the Eric-driven flows. For drafts the card itself is a
  // Link rather than a toggle button.
  const headerInner = (
    <>
      <Avatar name={customerName} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[15px] font-[620] text-foreground">
            {customerName ?? t('card.awaiting_label')}
          </span>
          {vehicleLabel ? (
            <>
              <span className="text-xs text-ink-3">·</span>
              <span className="truncate text-[13px] text-muted-foreground">
                {vehicleLabel}
              </span>
            </>
          ) : null}
          <StatusChip status={estimate.status} />
          {estimate.flag ? (
            <span className="rounded-full border border-attention-line bg-attention-soft px-[9px] py-[3px] text-[12px] font-[650] text-attention-text">
              {estimate.flag}
            </span>
          ) : null}
        </div>
        {estimate.work_description ? (
          <div className="mt-0.5 truncate text-[13px] text-muted-foreground">
            {estimate.work_description}
          </div>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[12px] text-ink-3">
          {t('card.total_label')}
        </div>
        <div
          className={
            'font-mono text-lg font-bold tabular-nums ' +
            (estimate.total !== null ? 'text-foreground' : 'text-ink-3')
          }
        >
          {estimate.total !== null ? formatMoney(estimate.total) : '—'}
        </div>
        <div className="text-[12px] text-ink-3">
          {formatRelative(estimate.created_at)}
        </div>
      </div>
      <ChevronDown
        className={
          'h-5 w-5 shrink-0 text-ink-3 transition-transform ' +
          (expanded && !isDraft ? 'rotate-180' : '')
        }
        aria-hidden="true"
      />
    </>
  )

  // §4.8 list row: 60px min-height, hairline separator, surface press
  // flash. The flag renders as the amber chip above — no rings, no
  // side-stripes.
  return (
    <div className="border-t border-border first:border-t-0">
      {isDraft ? (
        <Link
          to={`/estimates/${estimate.id}`}
          className="flex min-h-[60px] w-full items-center gap-3 px-3.5 py-3 text-left transition-[background-color,transform] active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
        >
          {headerInner}
        </Link>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-h-[60px] w-full items-center gap-3 px-3.5 py-3 text-left transition-[background-color,transform] active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
        >
          {headerInner}
        </button>
      )}

      {expanded && estimate.status === 'pending_approval' ? (
        <PendingBody estimate={estimate} onStubAction={onStubAction} />
      ) : null}
      {expanded && estimate.status === 'awaiting_customer_info' ? (
        <AwaitingBody estimate={estimate} />
      ) : null}
    </div>
  )
}

function vehicleSummary(
  t: TFunction<'estimates'>,
  e: Estimate,
): string | null {
  const parts = [e.vehicle_year, e.vehicle_make, e.vehicle_model].filter(Boolean)
  if (parts.length > 0) return parts.join(' ')
  if (e.customer_phone_pending)
    return t('card.phone_prefix', { phone: e.customer_phone_pending })
  return null
}

function Avatar({ name }: { name: string | null }) {
  const known = !!name && name.trim().length > 0
  const initial = known ? name!.trim().charAt(0).toUpperCase() : '?'
  // §4.8/§2.1 — neutral surface-2 avatar; unknown drops to ink-3.
  const className = known
    ? 'inline-flex size-[38px] shrink-0 items-center justify-center rounded-full bg-surface-2 text-[13px] font-bold text-muted-foreground'
    : 'inline-flex size-[38px] shrink-0 items-center justify-center rounded-full bg-surface-2 text-[13px] font-bold text-ink-3'
  return <span className={className}>{initial}</span>
}

// ─────────────────────────────────────────────────────────────────────
// Pending-approval expanded body

function PendingBody({
  estimate,
  onStubAction,
}: {
  estimate: Estimate
  onStubAction: () => void
}) {
  const { t } = useTranslation('estimates')
  const primary = estimate.parts?.primary ?? []
  const alternative = estimate.parts?.alternative ?? []
  const partsSource = estimate.parts?.source ?? null

  return (
    <div className="border-t border-border bg-muted/60">
      <div className="grid gap-6 px-3.5 py-4 md:grid-cols-2">
        {/* Parts column */}
        <div>
          <SectionHeading
            label={t('pending.parts_header')}
            sub={partsSource ? t('pending.parts_source', { source: partsSource }) : null}
          />
          <PartsList primary={primary} />
          {alternative.length > 0 ? <AlternativeDisclosure items={alternative} /> : null}
        </div>

        {/* Labor + totals column */}
        <div className="space-y-3">
          <div>
            <SectionHeading
              label={t('pending.labor_header')}
              sub={
                estimate.labor_confidence
                  ? t('pending.labor_confidence', { level: estimate.labor_confidence })
                  : null
              }
            />
            <LaborRow estimate={estimate} />
          </div>
          <Totals estimate={estimate} />
        </div>
      </div>

      {/* Action bar — gated on org role. Owners and mechanics see the
          full Decline / Modify / Approve buttons; receptionists see a
          read-only notice in the same slot. The Worker enforces the
          same role gate (Phase 3 mutation routes will use
          requireRole(ROLE_OWNER, ROLE_MECHANIC)) so a receptionist
          can't bypass via dev-tools. */}
      <ActionBar onStubAction={onStubAction} />
    </div>
  )
}

function ActionBar({ onStubAction }: { onStubAction: () => void }) {
  const { t } = useTranslation('estimates')
  const canApprove = useCanApproveEstimate()

  if (!canApprove) {
    return (
      <div className="border-t border-border bg-card px-3.5 py-4 text-[13px] text-muted-foreground">
        {t('pending.read_only_hint')}
      </div>
    )
  }

  return (
    <div className="border-t border-border bg-card px-3.5 py-4">
      <div className="mb-3 text-[13px] text-muted-foreground">
        {/* The "1" inside the hint is bold + mono. Use Trans so the
            translatable string stays whole and the styling slot is
            passed as a component. */}
        <Trans
          ns="estimates"
          i18nKey="pending.hint"
          components={{
            strong: (
              <span className="font-mono font-semibold text-foreground" />
            ),
          }}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="destructive" size="pill" onClick={onStubAction}>
          {t('pending.decline')}
        </Button>
        <Button variant="outline" size="pill" onClick={onStubAction}>
          {t('pending.modify')}
        </Button>
        <Button size="pill" onClick={onStubAction}>
          {t('pending.approve')}
        </Button>
      </div>
    </div>
  )
}

function SectionHeading({
  label,
  sub,
}: {
  label: string
  sub?: string | null
}) {
  return (
    <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
      {label}
      {sub ? (
        <span className="text-[12px] font-normal text-ink-3">
          {sub}
        </span>
      ) : null}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Parts — table at lg+, cards below

function PartsList({ primary }: { primary: PartItem[] }) {
  const { t } = useTranslation('estimates')
  if (primary.length === 0) {
    return (
      <div className="rounded-[10px] border border-dashed border-border bg-card p-4 text-center text-xs text-muted-foreground">
        {t('pending.parts_empty')}
      </div>
    )
  }
  return (
    <>
      {/* Stacked cards below the ≥1260px two-column width (§3.2) — the
          table only renders where the wide column gives it real room. */}
      <div className="space-y-2 min-[1260px]:hidden">
        {primary.map((p, i) => (
          <PartCard key={p.part_number ?? `p-${i}`} part={p} />
        ))}
      </div>
      {/* ≥1260px — table */}
      <div className="hidden overflow-hidden rounded-[10px] border border-border bg-card min-[1260px]:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted text-[12px] text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">
                {t('pending.table.part')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('pending.table.description')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('pending.table.qty_each')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('pending.table.total')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {primary.map((p, i) => (
              <PartRow key={p.part_number ?? `p-${i}`} part={p} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function partLabel(p: PartItem): string {
  return [p.brand, p.part_number].filter(Boolean).join(' · ') || '—'
}

function partLineTotal(p: PartItem): number | null {
  if (p.qty === null || p.qty === undefined) return null
  if (p.price_each === null || p.price_each === undefined) return null
  return p.qty * p.price_each
}

function PartCard({ part }: { part: PartItem }) {
  const total = partLineTotal(part)
  return (
    <div className="rounded-[10px] border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {partLabel(part)}
          </div>
          {part.description ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{part.description}</div>
          ) : null}
          {part.availability ? (
            <div className="mt-1 text-xs text-ink-3">{part.availability}</div>
          ) : null}
        </div>
        <div className="whitespace-nowrap text-right">
          <div className="font-mono text-[12px] tabular-nums text-ink-3">
            {part.qty ?? '—'} × {part.price_each !== null && part.price_each !== undefined
              ? formatMoney(part.price_each)
              : '—'}
          </div>
          <div className="font-mono text-sm font-semibold tabular-nums text-foreground">
            {total !== null ? formatMoney(total) : '—'}
          </div>
        </div>
      </div>
    </div>
  )
}

function PartRow({ part }: { part: PartItem }) {
  const total = partLineTotal(part)
  return (
    <tr>
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-foreground">{partLabel(part)}</div>
        {part.availability ? (
          <div className="text-xs text-ink-3">{part.availability}</div>
        ) : null}
      </td>
      <td className="px-3 py-2 align-top text-xs text-muted-foreground">
        {part.description ?? '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right align-top font-mono text-xs tabular-nums text-muted-foreground">
        {part.qty ?? '—'} ×{' '}
        {part.price_each !== null && part.price_each !== undefined
          ? formatMoney(part.price_each)
          : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right align-top font-mono font-semibold tabular-nums text-foreground">
        {total !== null ? formatMoney(total) : '—'}
      </td>
    </tr>
  )
}

function AlternativeDisclosure({ items }: { items: PartItem[] }) {
  const { t } = useTranslation('estimates')
  return (
    <details className="group mt-2 overflow-hidden rounded-[10px] bg-card/60">
      <summary className="flex cursor-pointer list-none select-none items-center gap-1.5 p-2 text-xs text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground">
        <ChevronRight
          className="h-3 w-3 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <span>{t('pending.alternative_disclosure')}</span>
      </summary>
      <div className="space-y-2 p-2 pt-0">
        {items.map((p, i) => {
          const total = partLineTotal(p)
          return (
            <div
              key={p.part_number ?? `alt-${i}`}
              className="rounded-[10px] border border-dashed border-border bg-card p-3 text-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-foreground">
                    {partLabel(p)}
                  </div>
                  {p.description ? (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {p.description}
                    </div>
                  ) : null}
                </div>
                <div className="whitespace-nowrap text-right">
                  <div className="font-mono text-[12px] tabular-nums text-ink-3">
                    {p.qty ?? '—'} ×{' '}
                    {p.price_each !== null && p.price_each !== undefined
                      ? formatMoney(p.price_each)
                      : '—'}
                  </div>
                  <div className="font-mono font-medium tabular-nums text-foreground">
                    {total !== null ? formatMoney(total) : '—'}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </details>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Labor + totals

function LaborRow({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation('estimates')
  const { t: tCommon } = useTranslation('common')
  const chip = laborSourceChip(tCommon, estimate.labor_source)
  const lineTotal =
    estimate.labor_subtotal ??
    (estimate.labor_hours !== null &&
    estimate.labor_hours !== undefined &&
    estimate.labor_rate !== null &&
    estimate.labor_rate !== undefined
      ? estimate.labor_hours * estimate.labor_rate
      : null)
  return (
    <div className="rounded-[10px] border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-foreground">
              {estimate.work_description ?? t('pending.labor_fallback_title')}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-[9px] py-[3px] text-[12px] font-[650] ${chip.className}`}
            >
              {chip.label}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t('pending.labor_line', {
              hours: estimate.labor_hours ?? 0,
              rate:
                estimate.labor_rate !== null && estimate.labor_rate !== undefined
                  ? formatMoney(estimate.labor_rate)
                  : '—',
            })}
          </div>
        </div>
        <div className="whitespace-nowrap font-mono font-semibold tabular-nums text-foreground">
          {lineTotal !== null ? formatMoney(lineTotal) : '—'}
        </div>
      </div>
    </div>
  )
}

function Totals({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation('estimates')
  const currency = estimate.currency ?? 'CAD'
  // Pass the per-row currency to formatMoney so a non-default currency
  // (e.g. USD on a cross-border estimate someday) renders correctly.
  const totalLine =
    estimate.total !== null
      ? formatMoney(estimate.total, { currency })
      : '—'
  return (
    // Neutral surface — a quote total is not a system save, so no green
    // and no legacy brand tint (§2).
    <div className="space-y-1.5 rounded-[10px] border border-border bg-muted p-3 text-sm">
      <TotalsRow label={t('pending.totals.parts')} value={estimate.parts_subtotal} />
      <TotalsRow label={t('pending.totals.labor')} value={estimate.labor_subtotal} />
      <TotalsRow label={t('pending.totals.subtotal')} value={estimate.subtotal} />
      <TotalsRow label={t('pending.totals.tax')} value={estimate.tax} />
      <div className="flex justify-between border-t border-input pt-1.5 font-bold text-foreground">
        <span>{t('pending.totals.total')}</span>
        <span className="font-mono tabular-nums">{totalLine}</span>
      </div>
    </div>
  )
}

function TotalsRow({
  label,
  value,
}: {
  label: string
  value: number | null
}) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="font-mono tabular-nums">{value !== null ? formatMoney(value) : '—'}</span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Awaiting-customer-info expanded body

const COLLECTED_FIELDS: Array<keyof Collected> = ['name', 'email', 'vin']

function AwaitingBody({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation('estimates')
  const collected = estimate.collected ?? {}
  return (
    <div className="border-t border-border bg-muted/60 px-3.5 py-4">
      <div className="mb-2 text-[13px] font-semibold text-muted-foreground">
        {t('awaiting.sms_header')}
      </div>
      <div className="rounded-[10px] border border-border bg-card p-3 text-sm italic leading-relaxed text-muted-foreground">
        {t('awaiting.sms_template')}
      </div>

      <div className="mt-3 grid gap-2 md:grid-cols-3">
        {COLLECTED_FIELDS.map((field) => {
          const raw = collected[field]
          const value = typeof raw === 'string' && raw.length > 0 ? raw : null
          return (
            <div
              key={field}
              className="rounded-[10px] border border-border bg-card p-2.5"
            >
              <div className="text-[12px] font-medium text-ink-3">
                {t(`awaiting.field.${field}`)}
              </div>
              <div
                className={
                  'mt-0.5 truncate text-sm ' +
                  (field === 'vin' && value ? 'font-mono tabular-nums ' : '') +
                  (value
                    ? 'font-medium text-foreground'
                    : 'italic text-ink-3')
                }
              >
                {value ?? t('awaiting.field_empty')}
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[12px] text-ink-3">
          {estimate.time_remaining
            ? t('awaiting.footer_with_remaining', {
                relative: formatRelative(estimate.created_at),
                remaining: estimate.time_remaining,
              })
            : t('awaiting.footer_without_remaining', {
                relative: formatRelative(estimate.created_at),
              })}
        </div>
        <button
          type="button"
          className="min-h-11 text-[14px] font-semibold text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
        >
          {t('awaiting.call_them')}
        </button>
      </div>
    </div>
  )
}
